import { useEffect, useState } from "react";
import { Card, Form, Offcanvas } from "react-bootstrap";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { toast } from "react-toastify";
import WebService from "../../utility/WebService";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import CustomButton from "../../components/CustomButton/CustomButton";

interface AddIesaIndustryExcellenceAwardProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  title: string;
  slug: string;
  createdBy: string;
  meta_description: string;
  keyword: string;
};

const AddIesaIndustryExcellenceAward = ({
  show,
  handleClose,
  data,
}: AddIesaIndustryExcellenceAwardProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();
  const [logo, setLogo] = useState<File | null>(null);
  const [about, setAbout] = useState(data?.about || "");
  const [categories, setCategories] = useState<any[]>([
    { image: null, title: "", buttonText: "", file: null },
  ]);
  const [pastGlimpse, setPastGlimpse] = useState<any[]>([
    { image: null, buttonText: "", link: "" },
  ]);
  const [pastWinners, setPastWinners] = useState<any[]>([
    { title: "", file: null },
  ]);
  const [videos, setVideos] = useState<any[]>([{ link: "" }]);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    if (data) {
      reset({
        title: data.title,
        slug: data.slug,
        keyword: data?.keyword,
        createdBy: userData?.fullName || "",
        meta_description: data?.meta_description,
      });
      setAbout(data.about || "");
      setCategories(
        data.categories && data.categories.length > 0
          ? data.categories
          : [{ image: null, title: "", buttonText: "", file: null }]
      );
      setPastGlimpse(
        data.pastGlimpse && data.pastGlimpse.length > 0
          ? data.pastGlimpse
          : [{ image: null, buttonText: "", link: "" }]
      );
      setPastWinners(
        data.pastWinners && data.pastWinners.length > 0
          ? data.pastWinners
          : [{ title: "", file: null }]
      );
      setVideos(
        data.video && data.video.length > 0 ? data.video : [{ link: "" }]
      );
    }
  }, [data, reset]);

  const onSubmit = (request: FormData) => {
    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    formData.append("title", request.title);
    formData.append("about", about);
    formData.append("meta_description", request.meta_description);
    formData.append("keyword", request.keyword);
    if (logo) {
      formData.append("logo", logo);
    }
    if (data?._id) {
      formData.append("id", data._id);
    }
    if (
      categories.length > 0 &&
      categories.every(
        (cat) =>
          cat.title.trim() !== "" ||
          cat.image ||
          cat.file ||
          cat.buttonText.trim() !== ""
      )
    ) {
      formData.append("categories", JSON.stringify(categories));
    }
    if (
      pastGlimpse.length > 0 &&
      pastGlimpse.every(
        (item) =>
          item.buttonText.trim() !== "" || item.link.trim() !== "" || item.image
      )
    ) {
      formData.append("pastGlimpse", JSON.stringify(pastGlimpse));
    }
    if (
      pastWinners.length > 0 &&
      pastWinners.every((item) => item.title.trim() !== "" || item.file)
    ) {
      formData.append("pastWinners", JSON.stringify(pastWinners));
    }
    if (videos.length > 0 && videos.every((item) => item.link.trim() !== "")) {
      formData.append("video", JSON.stringify(videos));
    }
    if (!data?._id && userData?._id) {
      formData.append("createdBy", userData._id);
    }
    WebService.fileUploadAPI({
      action: `iesa-industry-excellence-award/addContent`,
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message);
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  const uploadFile = (file: File, index: number, type: string) => {
    const formData = new FormData();
    formData.append("file", file);
    WebService.fileUploadAPI({
      action: `upload`,
      body: formData,
    })
      .then((res: any) => {
        if (res.data) {
          if (type === "CATEGORY") {
            categories[index].image = res.data?.url;
            setCategories([...categories]);
          } else if (type === "CATEGORY_FILE") {
            categories[index].file = res.data?.url;
            setCategories([...categories]);
          } else if (type === "PAST_GLIMPSE") {
            pastGlimpse[index].image = res.data?.url;
            setPastGlimpse([...pastGlimpse]);
          } else if (type === "PAST_WINNER") {
            pastWinners[index].file = res.data?.url;
            setPastWinners([...pastWinners]);
          } else if (type === "VIDEO") {
            videos[index].link = res.data?.url;
            setVideos([...videos]);
          }
        }
      })
      .catch((err: any) => {
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          handleClose();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{data ? "Edit" : "Add"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Main Content
              </span>

              <Form.Group className="mb-2" controlId="title">
                <Form.Label className="label">Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter title"
                  {...register("title", { required: "Title is required" })}
                />
                {errors.title && (
                  <div className="error-message">{errors.title.message}</div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="slugField">
                <Form.Label className="label">Slug</Form.Label>{" "}
                <span className="text-danger">*</span>
                <Form.Control
                  type="text"
                  placeholder="Auto-generated slug"
                  {...register("slug")}
                  value={watch("slug") || ""}
                  disabled // ✅ keep it readonly
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId="about">
                <Form.Label className="label">About</Form.Label>
                <ReactQuill
                  theme="snow"
                  value={about}
                  onChange={setAbout}
                  className="editor"
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId="categories">
                <Form.Label className="label">Categories</Form.Label>
                {categories.map((category, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("categoryIndex", index.toString());
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const fromIndex = Number(
                        e.dataTransfer.getData("categoryIndex")
                      );
                      if (fromIndex === index) return;
                      const newCategories = [...categories];
                      const [moved] = newCategories.splice(fromIndex, 1);
                      newCategories.splice(index, 0, moved);
                      setCategories(newCategories);
                    }}
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        style={{
                          cursor: "grab",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                          fontSize: 20,
                          color: "#888",
                          marginTop: 10,
                        }}
                        title="Drag to reorder"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <circle cx="5" cy="6" r="1.5" />
                          <circle cx="5" cy="10" r="1.5" />
                          <circle cx="5" cy="14" r="1.5" />
                          <circle cx="11" cy="6" r="1.5" />
                          <circle cx="11" cy="10" r="1.5" />
                          <circle cx="11" cy="14" r="1.5" />
                        </svg>
                      </span>
                      <div style={{ flex: 1 }} className="highlight-item">
                        <Form.Label className="label">Image</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          className="custom-image-upload"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files && input.files[0];
                            if (file) {
                              uploadFile(file, index, "CATEGORY");
                            }
                          }}
                        />
                        {categories[index] && (
                          <img
                            src={
                              categories[index].image
                                ? categories[index].image
                                : data?.categories?.[index]?.imageUrl || ""
                            }
                            style={{
                              maxWidth: "100%",
                              maxHeight: 100,
                              objectFit: "contain",
                              borderRadius: 8,
                              marginTop: 10,
                              marginBottom: 10,
                            }}
                          />
                        )}
                        <Form.Label className="label">File</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          className="custom-image-upload mb-2"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files && input.files[0];
                            if (file) {
                              uploadFile(file, index, "CATEGORY_FILE");
                            }
                          }}
                        />
                        <div className="d-flex">
                          <Form.Control
                            type="text"
                            placeholder={`Name`}
                            value={categories[index].title}
                            onChange={(e) => {
                              const newCategories = [...categories];
                              newCategories[index].title = e.target.value;
                              setCategories(newCategories);
                            }}
                          />
                          <div className="d-flex mt-2">
                            <PlusSquareFill
                              onClick={() => {
                                setCategories([
                                  ...categories,
                                  {
                                    image: null,
                                    title: "",
                                    buttonText: "",
                                    file: null,
                                  },
                                ]);
                              }}
                              className="ms-2 me-2 cursor-pointer"
                            />
                            {categories.length > 1 && (
                              <DashSquareFill
                                onClick={() => {
                                  const newCategories = categories.filter(
                                    (_, i) => i !== index
                                  );
                                  setCategories(newCategories);
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                        <Form.Control
                          type="text"
                          placeholder={`Button Text`}
                          value={categories[index].buttonText}
                          onChange={(e) => {
                            const newCategories = [...categories];
                            newCategories[index].buttonText = e.target.value;
                            setCategories(newCategories);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </Form.Group>
              <Form.Group className="mb-2" controlId="Past Glimpse">
                <Form.Label className="label">Past Glimpse</Form.Label>
                {pastGlimpse.map((past, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("pastIndex", index.toString());
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const fromIndex = Number(
                        e.dataTransfer.getData("pastIndex")
                      );
                      if (fromIndex === index) return;
                      const newPastGlimpse = [...pastGlimpse];
                      const [moved] = newPastGlimpse.splice(fromIndex, 1);
                      newPastGlimpse.splice(index, 0, moved);
                      setPastGlimpse(newPastGlimpse);
                    }}
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        style={{
                          cursor: "grab",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                          fontSize: 20,
                          color: "#888",
                          marginTop: 10,
                        }}
                        title="Drag to reorder"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <circle cx="5" cy="6" r="1.5" />
                          <circle cx="5" cy="10" r="1.5" />
                          <circle cx="5" cy="14" r="1.5" />
                          <circle cx="11" cy="6" r="1.5" />
                          <circle cx="11" cy="10" r="1.5" />
                          <circle cx="11" cy="14" r="1.5" />
                        </svg>
                      </span>
                      <div style={{ flex: 1 }} className="highlight-item">
                        <Form.Control
                          type="file"
                          accept="image/*"
                          className="custom-image-upload"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files && input.files[0];
                            if (file) {
                              uploadFile(file, index, "PAST_GLIMPSE");
                            }
                          }}
                        />
                        {pastGlimpse[index] && (
                          <img
                            src={
                              pastGlimpse[index].image
                                ? pastGlimpse[index].image
                                : data?.pastGlimpse?.[index]?.imageUrl || ""
                            }
                            style={{
                              maxWidth: "100%",
                              maxHeight: 100,
                              objectFit: "contain",
                              borderRadius: 8,
                              marginTop: 10,
                              marginBottom: 10,
                            }}
                          />
                        )}
                        <div className="d-flex">
                          <Form.Control
                            type="text"
                            placeholder={`Link`}
                            value={pastGlimpse[index].link}
                            onChange={(e) => {
                              const newPastGlimpse = [...pastGlimpse];
                              newPastGlimpse[index].link = e.target.value;
                              setPastGlimpse(newPastGlimpse);
                            }}
                          />
                          <div className="d-flex mt-2">
                            <PlusSquareFill
                              onClick={() => {
                                setPastGlimpse([
                                  ...pastGlimpse,
                                  { image: null, buttonText: "", link: "" },
                                ]);
                              }}
                              className="ms-2 me-2 cursor-pointer"
                            />
                            {pastGlimpse.length > 1 && (
                              <DashSquareFill
                                onClick={() => {
                                  const newPastGlimpse = pastGlimpse.filter(
                                    (_, i) => i !== index
                                  );
                                  setPastGlimpse(newPastGlimpse);
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                        <Form.Control
                          type="text"
                          placeholder={`Button Text`}
                          value={pastGlimpse[index].buttonText}
                          onChange={(e) => {
                            const newPastGlimpse = [...pastGlimpse];
                            newPastGlimpse[index].buttonText = e.target.value;
                            setPastGlimpse(newPastGlimpse);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </Form.Group>
              <Form.Group className="mb-2" controlId="Past Winners">
                <Form.Label className="label">Past Winners</Form.Label>
                {pastWinners.map((past, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("pastIndex", index.toString());
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const fromIndex = Number(
                        e.dataTransfer.getData("pastIndex")
                      );
                      if (fromIndex === index) return;
                      const newPastWinners = [...pastWinners];
                      const [moved] = newPastWinners.splice(fromIndex, 1);
                      newPastWinners.splice(index, 0, moved);
                      setPastWinners(newPastWinners);
                    }}
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        style={{
                          cursor: "grab",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                          fontSize: 20,
                          color: "#888",
                          marginTop: 10,
                        }}
                        title="Drag to reorder"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <circle cx="5" cy="6" r="1.5" />
                          <circle cx="5" cy="10" r="1.5" />
                          <circle cx="5" cy="14" r="1.5" />
                          <circle cx="11" cy="6" r="1.5" />
                          <circle cx="11" cy="10" r="1.5" />
                          <circle cx="11" cy="14" r="1.5" />
                        </svg>
                      </span>
                      <div style={{ flex: 1 }} className="highlight-item">
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          className="custom-image-upload"
                          onChange={(e) => {
                            const input = e.target as HTMLInputElement;
                            const file = input.files && input.files[0];
                            if (file) {
                              uploadFile(file, index, "PAST_WINNER");
                            }
                          }}
                        />

                        {/* Show file link or url if available */}
                        {(pastWinners[index]?.file ||
                          data?.pastWinners?.[index]?.file) && (
                          <div className="mt-2">
                            <a
                              href={
                                pastWinners[index]?.file ||
                                data?.pastWinners?.[index]?.file
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ wordBreak: "break-all" }}
                            >
                              {pastWinners[index]?.file ||
                                data?.pastWinners?.[index]?.file}
                            </a>
                          </div>
                        )}

                        <div className="d-flex">
                          <Form.Control
                            type="text"
                            placeholder={`Title`}
                            value={pastWinners[index].title}
                            onChange={(e) => {
                              const newPastWinners = [...pastWinners];
                              newPastWinners[index].title = e.target.value;
                              setPastWinners(newPastWinners);
                            }}
                          />
                          <div className="d-flex mt-2">
                            <PlusSquareFill
                              onClick={() => {
                                setPastWinners([
                                  ...pastWinners,
                                  { title: "", file: null },
                                ]);
                              }}
                              className="ms-2 me-2 cursor-pointer"
                            />
                            {pastWinners.length > 1 && (
                              <DashSquareFill
                                onClick={() => {
                                  const newPastWinners = pastWinners.filter(
                                    (_, i) => i !== index
                                  );
                                  setPastWinners(newPastWinners);
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Form.Group>

              <Form.Group className="mb-2" controlId="createdBy">
                <Form.Label className="label">Created By</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Created by user"
                  {...register("createdBy")}
                  value={watch("createdBy") || ""}
                  disabled
                />
              </Form.Group>
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Media
              </span>
              <Form.Group className="mb-2" controlId="banner">
                <Form.Label className="label">Banner</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  className="custom-image-upload"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const file = input.files && input.files[0];
                    if (file) {
                      setLogo(file);
                    }
                  }}
                />
              </Form.Group>
              {(logo || data?.logo) && (
                <div className="mb-2 event-image">
                  <img
                    src={logo ? URL.createObjectURL(logo) : data?.logo}
                    alt="Event"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
              <Form.Group className="mb-2" controlId="Videos">
                <Form.Label className="label">Videos</Form.Label>
                {videos.map((video, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("videoIndex", index.toString());
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const fromIndex = Number(
                        e.dataTransfer.getData("videoIndex")
                      );
                      if (fromIndex === index) return;
                      const newVideos = [...videos];
                      const [moved] = newVideos.splice(fromIndex, 1);
                      newVideos.splice(index, 0, moved);
                      setVideos(newVideos);
                    }}
                    style={{ cursor: "move" }}
                  >
                    <div className="d-flex align-items-start">
                      <span
                        style={{
                          cursor: "grab",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                          fontSize: 20,
                          color: "#888",
                          marginTop: 10,
                        }}
                        title="Drag to reorder"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <circle cx="5" cy="6" r="1.5" />
                          <circle cx="5" cy="10" r="1.5" />
                          <circle cx="5" cy="14" r="1.5" />
                          <circle cx="11" cy="6" r="1.5" />
                          <circle cx="11" cy="10" r="1.5" />
                          <circle cx="11" cy="14" r="1.5" />
                        </svg>
                      </span>
                      <div style={{ flex: 1 }} className="highlight-item">
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="file"
                            accept="video/*"
                            className="custom-image-upload"
                            onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files && input.files[0];
                              if (file) {
                                uploadFile(file, index, "VIDEO");
                              }
                            }}
                          />
                          <div className="d-flex mt-2">
                            <PlusSquareFill
                              onClick={() => {
                                setVideos([...videos, { link: "" }]);
                              }}
                              className="ms-2 me-2 cursor-pointer"
                            />
                            {videos.length > 1 && (
                              <DashSquareFill
                                onClick={() => {
                                  const newVideos = videos.filter(
                                    (_, i) => i !== index
                                  );
                                  setVideos(newVideos);
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </div>
                        {videos[index]?.link && (
                          <div className="mt-2">
                            <Form.Control
                              type="text"
                              value={videos[index].link}
                              readOnly
                              className="mb-2"
                            />
                            <video
                              src={videos[index].link}
                              controls
                              style={{
                                maxWidth: "100%",
                                maxHeight: 200,
                                borderRadius: 8,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Form.Group>
            </Card>
            
             <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              SEO
            </span>
              <Form.Label className="label">
                Meta Description
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Meta Description"
                {...register("meta_description", {
                  required: "Meta description is required",
                })}
              />
              {errors.meta_description && (
                <div className="error-message">
                  {errors.meta_description.message}
                </div>
              )}
              <Form.Group className="mb-2" controlId="eventName">
                <Form.Label className="label">
                  Keywords <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter keywords"
                  {...register("keyword", {
                    required: "Keywords are required",
                  })}
                />
                {errors.keyword && (
                  <div className="error-message">{errors.keyword.message}</div>
                )}
              </Form.Group>
            </Card>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );   
};

export default AddIesaIndustryExcellenceAward;
